import React from 'react';
import ReactMarkdown from 'react-markdown';

const content = `
**PRIVACY NOTICE**
**Last updated June 26, 2024**

This privacy notice for CRWD.tech, LLC ("**Company** ," "**we**," "**us**," or "**our**" ), descri bes how and why we might collect, store, use, and/or share ("**process**") your information when you use our services ("**Services**"), such as when you:

-   Visit our website at canna42.com , or any website of ours that links to this privacy notice

-   Download and use our mobile application (Canna42) , or any other application of ours that links to this privacy notice

-   Engage with us in other related ways, including any sales, marketing, or events

**Questions or concerns? **Reading this privacy notice will help you understand your privacy right s and choices. If you do not agree with our policies and practices, please do not use our Services. If you still have any questions or concerns, please contact us at support@canna42.tech.

**SUMMARY OF KEY POINTS**

***This summary provides key points from our privacy notice, but you can find out more details about any of Privacy Policy v1 canna42.docx these topics by clicking the link fol lowing each key point or by using our table of contents below to find the section you are looking for. You can also click ***[***here***](https://canna42-privacy.netlify.app/#toc)*** to go directly to our table of contents.***

**What personal information do we process?** When you visit, use, or navigate our Services, we may process personal information depending on how you interact with CRWD.tech, LLC and the Services, the choices you make, and the products and features you use. Click [here](https://canna42-privacy.netlify.app/#personalinfo) to learn more.

**Do we process any sensitive personal information?** We may process sensitive personal information when necessary with your consent or as otherwise permitted by applicable law. Click [here](https://canna42-privacy.netlify.app/#sensitiveinfo) to learn more.

**Request for Deletion of Your Personal Information:** You have the right to request the deletion of your personal data that we hold. For details on how to make this request, please see the full section below or contact us directly at account@canna42.com.

**Do we receive any information from third parties?** We do not receive any information from third parties.

**How do we process your information?** We process your information to provide, improve, and administer our Services, communicate with you, for security an d fraud prevention, and to comply with law. We may also process your information for other purposes with your consent. We process your information only when we have a valid legal reason to do so. Click [here](https://canna42-privacy.netlify.app/#infou%20%20%20%20se) to learn more.

**In what situations and with which types of parties do we share personal information? We may share information in specific situations and with specific categories of third parties. Click ****[here](https://canna42-privacy.netlify.app/#whoshare) to learn more.**

**How do we keep your information safe?** We have organizational and technical processes and procedures in place to protect your personal information. However, no electronic transmission over the internet or information storage technology can be guaranteed to be 100% secure, so we cannot promise or guarantee that hack ers, cybercriminals, or other unauthorized third parties will not be able to defeat our security and improperly collect, access, steal, or modify your information. Click [here](https://canna42-privacy.netlify.app/#infosafe) to learn more.

**What are your rights?** Depending on where you are located geograph ically, the applicable privacy law may mean you have certain rights regarding your personal information. Click [here](https://canna42-privacy.netlify.app/#privacyrights) to learn more.

**How do I exercise my rights?** The easiest way to exercise your rights is by filling out our data subject request form available here: canna42.com/data - request , or by contacting us. We will consider and act upon any request in accordance with applicable data protection laws.

Want to learn more about what CRWD.tech, LLC does with any information we collect? Click [here](https://canna42-privacy.netlify.app/#toc) to review the notice in full.

**TABLE OF CONTENTS**

[1. WHAT INFORMATION DO WE COLLECT?](https://canna42-privacy.netlify.app/#infocollect)

[2. HOW DO WE PROCESS YOUR INFORMATION?](https://canna42-privacy.netlify.app/#infouse)

[3. WHAT LEGAL BASES DO WE RELY ON TO PROCESS YOUR PERSONAL INFORMATION?](https://canna42-privacy.netlify.app/#legalbases)

[4. WHEN AND WITH WHOM DO WE SHARE YOUR PERSONAL INFORMATION?](https://canna42-privacy.netlify.app/#whoshare)

[5. HOW LONG DO WE KEEP YOUR INFORMATION?](https://canna42-privacy.netlify.app/#inforetain)

[6. HOW DO WE KEEP YOUR INFORMATION SAFE?](https://canna42-privacy.netlify.app/#infosafe)

[7. DO WE COLLECT INFORMATION FROM MINORS?](https://canna42-privacy.netlify.app/#infominors)

[8. WHAT ARE YOUR PRIVACY RIGHTS?](https://canna42-privacy.netlify.app/#privacyrights)

[9. CONTROLS FOR DO - NOT - TRACK FEATURES](https://canna42-privacy.netlify.app/#DNT)

[10. DO CALIFORNIA RESIDENTS HAVE SPECIFIC PRIVACY RIGHTS?](https://canna42-privacy.netlify.app/#caresidents)

[11. DO WE MAKE UPDATES TO THIS NOTICE?](https://canna42-privacy.netlify.app/#policyupdates)

[12. HOW CAN YOU CONTACT US ABOUT THIS NOTICE?](https://canna42-privacy.netlify.app/#contact)

[13. HOW CAN YOU REVIEW, UPDATE, OR DELETE THE DATA WE COLLECT FROM YOU?](https://canna42-privacy.netlify.app/#request)

**1. WHAT INFORMATION DO WE COLLECT?**

**Personal information you disclose to us**

**

***In Short:*** **We collect personal information that you provide to us.**

*

We collect personal information that you voluntarily provide to us when you register on the Services, express an interest in obtaining information about us or our products and Services, when you participate in activities on the Services, or otherwise when you contact us.

**Personal Information Provided by You.** The personal information that we collect depends on the con text of your interactions with us and the Services, the choices you make, and the products and features you use. The personal information we collect may include the following:

-   names

-   email addresses

-   mailing addresses

-   phone nu mbers

-   usernames

-   passwords

-   contact preferences

-   billing addresses

-   debit/credit card numbers

-   contact or authentication data

-   photos

-   geolocation

-   in app activity

-   personal preferences

Sensitive Information. When necessary, with your consent or as otherwise permitted by applicable law, we process the following categories of sensitive information:

-   data about a person's sex life or sexual orientation

**Payment Data.** We may collect data necessary to process your payment if you make purchases, such as your payment instrument number (such as a credit card number), and the security code associated with your payment instrument. All payment data is stored by stripe . You may find their privacy notice link(s) here: [https://stripe.com/privacy](https://stripe.com%20%20%20%20/privacy).

**Application Data.** If you use our application(s), we also may collect the following information if you choose to provide u s with access or permission:

-   *Geolocation Information.* We may request access or permission to track location - based information from your mobile device, either continuously or while you are using our mobile application(s), to provide certain location - based services. If you wish to change our access or permissions, you may do so in your device's set tings.

-   *Mobile Device Access.* We may request access or permission to certain features from your mobile device, including your mobile device's **calendar, camera, contacts, reminders, social media accounts, storage, and other features. If you wish to change our access or permissions, you may do so in your device's settings.**

**

-   *Mobile Device Data.* We automatically collect device information (such as your mobile device ID, model, and manufacturer), o perating system, version information and system configuration information, device and application identification numbers, browser type and version, hardware model Internet service provider and/or mobile carrier, and Internet Privacy Policy v1 canna42.docx Protocol (IP) address (or proxy server). If you are using our application(s), we may also collect information about the phone network associated with your mobile device, your mobile device ' s operating system or platform, the type of mobile device you use, your mobile device ' s unique dev ice ID, and information about the features of our application(s) you accessed.

-   *Push Notifications.* We may request to send you push notifications regarding your account or certain f eatures of the application(s). If you wish to opt out from receiving these types of communications, you may turn them off in your device's settings.

This information is primarily needed to maintain the security and operation of our application(s), for troubleshooting, and for our internal analytics and reporting purposes.

 **

*

 ***

All personal information that you provide to us must be true, complete, and accurate, and you must notify us of any changes to such personal information.

**Information automatically collected**

***In Short:*** *Some information --- such as your Internet Protocol (IP) address and/or browser and device characteristics --- is collected automatically when you visit our Services.*

We automatically collect certain information when you visit, use, or navigate the Services. This information does not reveal your specific identity (like your name or contact information) but may include device and usage information, such as your IP address, browser and device characteristics, operating system, language preferences, referring URLs, device name, country, location, information about how and when you use our Services, and other technical information. This inf ormation is primarily needed to maintain the security and operation of our Services, and for our internal analytics and reporting purposes.

The information we collect includes:

-   *Log and Usage Data.* Log and usage data is service - related, diagnostic, usage, and performance information our servers automatically collect when you access or us e our Services and which we record in log files. Depending on how you interact with us, this log data may include your IP address, device information, browser type, and settings and information about your activity in the Services (such as the date/time stamps associated with your usage, pages and files viewed, searches, and other actions you take such as which features you use), device event information (such as system activity, error reports (sometimes called "crash dumps"), and hardware settings).

-   *Device Data.* We collect devic e data such as information about your computer, phone, tablet, or other device you use to access the Services. Depending on the device used, this device data may include information such as your IP address (or proxy server), device and application identifi cation numbers, location, browser type, hardware model, Internet service provider and/or mobile carrier, operating system, and system configuration information.

-   *Location Data.* We collect location data such as information about your device's location, which can be either precise or imprecise. How much information we collect depends on the type and settings of the device you use to access the Ser vices. For example, we may use GPS and other technologies to collect geolocation data that tells us your current location (based on your IP address). You can opt out of allowing us to collect this information either by refusing access to the information or by disabling your Location setting on your device. However, if you choose to opt out, you may not be able to use certain aspects of the Services.

**2. HOW DO WE PROCESS YOUR INFORMATION?**

 ***In Short: ****We process your information to provide, improve, and administer our Services, communicate with you, for security and fraud prevention, an d to comply with law. We may also process your information for other purposes with your consent.*

 **We process your personal information for a variety of reasons, depending on how you interact with our Services, including:**

-   **To fac ilitate account creation and authentication and otherwise manage user accounts. **We may process your information so you can create and log in to your account, as well as keep your account in working order.

-   **To deliver and facilitate delivery of services to the user. **We may process your information to provide you with the requested service.

-   **To respond to user inquiries/offer support to users. **We may process y our information to respond to your inquiries and solve any potential issues you might have with the requested service.

**3. REQUEST FOR DELETION OF PERSONAL INFORMATION**

***In Short: ****We are committed to ensuring your right to privacy and understand the importance of maintaining control over your personal information. If you wish to request the deletion of your personal data that we have collected and hold, please send an email to account@canna42.com with the subject line "Request for Deletion of Personal Information." Please include in your email the specifics of your request and any other information that may help us to identify you and process your request in a timely manner. We will review your request and respond as quickly as possible, detailing the steps we have taken to address your concerns.*

***
`

export const PrivacyPolicy = () => {
    return (
        <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px',
            color: 'black'
        }}>
            <ReactMarkdown
                className="text-white"
                components={
                    {
                        // eslint-disable-next-line jsx-a11y/heading-has-content
                        h1: ({ node, ...props }) => <h1 style={{ color: 'black' }} {...props} />,
                        // eslint-disable-next-line jsx-a11y/heading-has-content
                        h2: ({ node, ...props }) => <h2 style={{ color: 'black' }} {...props} />,
                        // eslint-disable-next-line jsx-a11y/heading-has-content
                        h3: ({ node, ...props }) => <h3 style={{ color: 'black' }} {...props} />,
                        // eslint-disable-next-line jsx-a11y/heading-has-content
                        h4: ({ node, ...props }) => <h4 style={{ color: 'black' }} {...props} />,
                        // eslint-disable-next-line jsx-a11y/heading-has-content
                        h5: ({ node, ...props }) => <h5 style={{ color: 'black' }} {...props} />,
                        // eslint-disable-next-line jsx-a11y/heading-has-content
                        h6: ({ node, ...props }) => <h6 style={{ color: 'black' }} {...props} />,
                        p: ({ node, ...props }) => <p style={{ color: 'black' }} {...props} />,
                        // eslint-disable-next-line jsx-a11y/heading-has-content, jsx-a11y/anchor-has-content
                        a: ({ node, ...props }) => <a style={{ color: 'black' }} {...props} />,
                        ul: ({ node, ...props }) => <ul style={{ color: 'black' }} {...props} />,
                        ol: ({ node, ...props }) => <ol style={{ color: 'black' }} {...props} />,
                        li: ({ node, ...props }) => <li style={{ color: 'black' }} {...props} />,
                        blockquote: ({ node, ...props }) => <blockquote style={{ color: 'black' }} {...props} />,
                        code: ({ node, ...props }) => <code style={{ color: 'black' }} {...props} />,
                        pre: ({ node, ...props }) => <pre style={{ color: 'black' }} {...props} />,
                        table: ({ node, ...props }) => <table style={{ color: 'black' }} {...props} />,
                        th: ({ node, ...props }) => <th style={{ color: 'black' }} {...props} />,
                        td: ({ node, ...props }) => <td style={{ color: 'black' }} {...props} />,
                        tr: ({ node, ...props }) => <tr style={{ color: 'black' }} {...props} />,
                        // eslint-disable-next-line jsx-a11y/heading-has-content, jsx-a11y/anchor-has-content, jsx-a11y/alt-text
                        img: ({ node, ...props }) => <img style={{ color: 'black' }} {...props} />,
                    }
                }
            >
                {content}
            </ReactMarkdown>
        </div>
    )
}
